/**
 * VMware (Enterprise Compute / Bear) — shared data-layer infra for the VMware
 * console. Ported from the legacy @console `Internal.VMware` connectors.
 *
 * VMware (s10006) traffic enters the @mgmt v3 strangler gateway, which authorizes
 * the request then forwards to the legacy Ruby app. The FE path is kept uniform
 * with OpenNebula (s10003):
 *   `${serviceApiV3}/services/:serviceId/s10006/<path>`  (base already includes /api/v3)
 *
 * NOTE on the backend asymmetry (hidden from here): Ruby mounts VMware as a
 * controller namespace, not a URL segment, so the @mgmt proxy STRIPS `s10006`
 * and forwards to the bare legacy `/api/v2/services/:id/instances/*`. The FE never
 * sees that — it always talks `s10006`, exactly like s10003.
 *
 * VMware ≠ OpenNebula in features: it has Overview+resize, Monitoring, sFlow
 * network-traffic and Veeam backup, but NO create/clone, volumes, snapshots,
 * secgroups, VPC or load balancers. Keep this module scoped to what s10006 serves.
 */
import { serviceApiV3 } from '@/api/client';
import type { AxiosResponse } from 'axios';

// ---- Service scope + path ------------------------------------------------

/** Provider scope that resolves to this module (see services/categories.ts). */
export const VMWARE_SCOPE = 'Internal.VMware';

/** VMware IaaS provider code (preserved on the v3 gateway path; proxy strips it). */
const S10006 = 's10006';

/** Build a service-scoped path: vmPath(id, 'instances') → /services/id/s10006/instances */
export function vmPath(serviceId: string, path: string): string {
  return `/services/${serviceId}/${S10006}/${path.replace(/^\/+/, '')}`;
}

/** The compute gateway client (@mgmt v3 strangler proxy). Use with `vmPath()`. */
export const serviceApi = serviceApiV3;

// ---- Response envelope ---------------------------------------------------
// s10006 wraps payloads as `{ status, data, message?, total? }` (same Ruby app as
// s10003). `unwrap` returns the inner `data`, falling back to the raw body.

export interface VmEnvelope<T> {
  status?: number;
  data: T;
  message?: string;
  total?: number;
}

export function unwrap<T>(res: AxiosResponse<VmEnvelope<T> | T>): T {
  const body = res.data as unknown;
  if (body && typeof body === 'object' && 'data' in (body as Record<string, unknown>)) {
    return (body as VmEnvelope<T>).data;
  }
  return body as T;
}

// ---- Query keys ----------------------------------------------------------
// Array-based keys (TanStack v5 convention), scoped by serviceId. Namespace `vm`
// keeps them distinct from OpenNebula's `on` keys.

export const vmKeys = {
  all: ['vm'] as const,
  service: (s: string) => ['vm', s] as const,

  instances: (s: string) => ['vm', s, 'instances'] as const,
  instance: (s: string, id: string) => ['vm', s, 'instances', id] as const,
  instanceInfo: (s: string, id: string) => ['vm', s, 'instances', id, 'info'] as const,
  instanceSummary: (s: string, id: string) => ['vm', s, 'instances', id, 'summary'] as const,
  instanceMonitoring: (s: string, id: string, q?: Record<string, unknown>) =>
    ['vm', s, 'instances', id, 'stats', JSON.stringify(q ?? {})] as const,
  instanceRemote: (s: string, id: string) => ['vm', s, 'instances', id, 'ticket'] as const,
  instanceSflow: (s: string, id: string, q?: Record<string, unknown>) =>
    ['vm', s, 'instances', id, 'sflow', JSON.stringify(q ?? {})] as const,
  instanceSflowSources: (s: string, id: string, q?: Record<string, unknown>) =>
    ['vm', s, 'instances', id, 'sflow_sources', JSON.stringify(q ?? {})] as const,

  // Veeam backup
  backupSummary: (s: string, id: string) => ['vm', s, 'instances', id, 'backup', 'summary'] as const,
  backupObject: (s: string, id: string) => ['vm', s, 'instances', id, 'backup', 'object'] as const,
  backupJobs: (s: string, id: string) => ['vm', s, 'instances', id, 'backup', 'jobs'] as const,
  backupJobStatus: (s: string, id: string) => ['vm', s, 'instances', id, 'backup', 'job_status'] as const,
  backupRestorePoints: (s: string, id: string, limit: number) =>
    ['vm', s, 'instances', id, 'backup', 'restore_points', limit] as const,
  backupSessions: (s: string, id: string, limit: number) =>
    ['vm', s, 'instances', id, 'backup', 'sessions', limit] as const,
  backupRepositories: (s: string, id: string) => ['vm', s, 'instances', id, 'backup', 'repositories'] as const,
} as const;

// ---- Instance lifecycle types --------------------------------------------

/**
 * Actions POSTed to `/instances/:id/perform` as `{ perform, context? }`.
 * VMware uses vSphere verbs (start / reboot / shutdown / standby) — NOT the
 * OpenNebula set. `standby` = suspend (VMRC verb matching @console behaviour).
 * `update` carries a resize context (cpu/memory/disk).
 */
export type VmwareAction = 'start' | 'reboot' | 'shutdown' | 'standby' | 'update';

/** External "an action is in flight" flag that drives adaptive polling. */
export type WaitingState = boolean | VmwareAction | string;

// ---- Adaptive polling ----------------------------------------------------

interface ActionPollingConfig {
  initialInterval: number;
}

const ACTION_POLLING_CONFIG: Record<VmwareAction | 'default', ActionPollingConfig> = {
  start: { initialInterval: 2000 },
  reboot: { initialInterval: 2000 },
  shutdown: { initialInterval: 3000 },
  standby: { initialInterval: 3000 },
  update: { initialInterval: 3000 },
  default: { initialInterval: 3000 },
};

/** Polling interval for a react-query `refetchInterval`. `false` = idle. */
export function getPollingInterval(waiting: WaitingState): number | false {
  if (!waiting) return false;
  if (typeof waiting === 'string') {
    return (ACTION_POLLING_CONFIG[waiting as VmwareAction] ?? ACTION_POLLING_CONFIG.default).initialInterval;
  }
  return ACTION_POLLING_CONFIG.default.initialInterval;
}
